<template>
  <div
    v-if="bannerConfig"
    class="w-full max-w-full overflow-x-hidden"
  >
    <div
      v-if="bannerConfig.variant === 'services'"
      class="w-full border border-[#E7E7E8] bg-gold-primary-10 px-3 py-2 services-banner-height"
    >
      <div class="flex items-center justify-between h-full">
        <div class="flex items-center gap-3">
          <div
            class="rounded-full px-3 py-2 flex items-center gap-2 text-sm shadow-sm cursor-pointer transition-colors duration-200"
            :class="showUsedOnly ? 'bg-[#FFB648]' : 'bg-[#F8F7F3]'"
            @click="toggleServicesFilter"
          >
            <span class="text-sm">{{ servicesPillText }}</span>
          </div>
        </div>
        <div class="flex items-center gap-3">
          <div class="text-sm text-black-90">{{ t('shell.mobileBanner.insiderLevel') }}</div>
          <div
            class="rounded-full w-8 h-8 flex items-center justify-center relative"
            :class="badgeColor ? '' : 'bg-[#F8F7F3]'"
            :style="badgeColor ? { backgroundColor: `${badgeColor}20` } : {}"
          >
            <UIBadgeIcon
              :badge-number="badgeNumber"
              :color="badgeColor || '#141B34'"
              size="24px"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-else-if="bannerConfig.variant === 'prelaunch'"
      class="w-full bg-[#FFE9C8] px-4 py-4 border-b border-[#E7E7E8]"
    >
      <div
        class="flex cursor-pointer items-center justify-between"
        @click="toggleExpanded"
      >
        <h3 class="font-bold text-black-90 text-[14px] leading-[140%] prelaunch-title">{{ bannerConfig.title }}</h3>
        <UIIcon
          name="chevron-big-up"
          class="text-black-90 transform transition-transform duration-200"
          :class="[isExpanded ? 'rotate-0' : 'rotate-180']"
        />
      </div>

      <Transition
        enter-active-class="transition-all duration-300 ease-out"
        enter-from-class="max-h-0 opacity-0"
        enter-to-class="max-h-[260px] opacity-100"
        leave-active-class="transition-all duration-300 ease-in"
        leave-from-class="max-h-[260px] opacity-100"
        leave-to-class="max-h-0 opacity-0"
      >
        <div
          v-if="isExpanded"
          class="overflow-hidden"
        >
          <div class="pt-2.5 text-center">
            <p class="mb-4 text-sm text-black-70 text-center">{{ bannerConfig.description }}</p>

            <div class="border-t border-[#FFC56D] pt-3">
              <UIButton
                v-if="bannerConfig.buttonText"
                id="banner-action"
                class="w-full flex items-center justify-center gap-2 !bg-[#2BABEE] !border !border-[#2BABEE] hover:!bg-[#2396D1] rounded-xl min-h-[44px]"
                @click="handleButtonClick"
              >
                <span class="flex items-center gap-2">
                  <UIIcon
                    :name="BANNER_CONSTANTS.TELEGRAM.ICON"
                    size="xs"
                    class="!size-4"
                  />
                  <span class="font-semibold text-[14px] leading-[100%] text-[#197BAE] open-sans">{{
                    bannerConfig.buttonText
                  }}</span>
                </span>
              </UIButton>
            </div>

            <div class="text-center mt-3 text-sm">
              <span class="text-black-70">{{ t(BANNER_CONSTANTS.UPGRADE.TEXT) }}&nbsp;</span>
              <button
                class="font-bold underline cursor-pointer"
                @click.stop="handleUpgradeClick"
              >
                {{ t(BANNER_CONSTANTS.UPGRADE.LINK_TEXT) }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </div>

    <div
      v-else-if="bannerConfig.variant === 'training'"
      class="w-full border border-[#E7E7E8] bg-gold-primary-10 px-3 py-2 services-banner-height"
    >
      <div class="flex items-center justify-between h-full">
        <div class="text-sm text-black-90 font-medium bg-[#F8F7F3] px-3 py-2 rounded-full">
          {{ bannerConfig.title }}
        </div>
        <div class="flex items-center gap-2 text-sm text-black-90">
          <span class="text-sm">{{ t('shell.mobileBanner.insiderLevel') }}</span>
          <div
            class="rounded-full w-8 h-8 flex items-center justify-center relative"
            :class="badgeColor ? '' : 'bg-[#F8F7F3]'"
            :style="badgeColor ? { backgroundColor: `${badgeColor}20` } : {}"
          >
            <UIBadgeIcon
              :badge-number="badgeNumber"
              :color="badgeColor || '#141B34'"
              size="24px"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      class="w-full bg-[#FFE9C8] px-4 py-4 border-b border-[#E7E7E8]"
    >
      <div
        class="flex cursor-pointer items-center justify-between gap-2.5"
        @click="toggleExpanded"
      >
        <div class="flex-1">
          <h3 class="font-semibold text-black-90 text-sm">{{ bannerConfig.title }}</h3>
        </div>
        <UIIcon
          :name="isExpanded ? 'chevron-big-filled-up' : 'chevron-big-filled-down'"
          class="text-black-60 transition-transform duration-200"
        />
      </div>

      <Transition
        enter-active-class="transition-all duration-300 ease-out"
        enter-from-class="max-h-0 opacity-0"
        enter-to-class="max-h-96 opacity-100"
        leave-active-class="transition-all duration-300 ease-in"
        leave-from-class="max-h-96 opacity-100"
        leave-to-class="max-h-0 opacity-0"
      >
        <div
          v-if="isExpanded"
          class="overflow-hidden"
        >
          <div class="pt-2.5">
            <p class="mb-4 text-sm text-black-70">{{ bannerConfig.description }}</p>

            <UIButton
              v-if="bannerConfig.buttonText"
              id="banner-action"
              class="w-full !bg-[#2BABEE] hover:!bg-[#2396D1]"
              @click="handleButtonClick"
            >
              <UIIcon
                :name="BANNER_CONSTANTS.TELEGRAM.ICON"
                class="me-2"
              />
              {{ bannerConfig.buttonText }}
            </UIButton>
          </div>
        </div>
      </Transition>
    </div>

    <TrainingUpgradeModal
      v-model="showUpgradeModal"
      :initial-tier="'gold'"
      @upgrade="handleUpgrade"
      @see-more="handleSeeMore"
    />
  </div>
</template>

<script setup lang="ts">
import { BANNER_CONSTANTS } from '@/constants/banners';
import { useUserServices } from '@/api/queries/services';
import { useServicesFilter } from '@/composables/useServicesFilter';
import { useInsiderLevel } from '@/composables/useInsiderLevel';
import TrainingUpgradeModal from '@/components/training/UpgradeModal.vue';

interface BannerConfig {
  variant?: 'prelaunch' | 'services' | 'training' | 'default';
  title?: string;
  description?: string;
  buttonText?: string;
  buttonAction?: () => void;
  leftPillText?: string;
  rightText?: string;
}

type ServiceWithUsage = { usedCount: number };

const props = defineProps<{
  bannerConfig?: BannerConfig | null;
}>();

const { t } = useI18n();
const isExpanded = ref(false);
const showUpgradeModal = ref(false);
const { showUsedOnly, toggleUsedOnly } = useServicesFilter();
const { badgeNumber, badgeColor } = useInsiderLevel();
const authStore = useAuthStore();

const { data: services } = useUserServices();

const isPremiumUser = computed(() => {
  const user = authStore.getUser;
  if (!user) return false;
  return !!(user as any)?.packageId;
});

const usedServices = computed<ServiceWithUsage[]>(() => {
  if (!services.value || !Array.isArray(services.value)) return [];
  return services.value.filter((service: any) => (service.usedCount || 0) > 0);
});

const servicesPillText = computed(() => {
  const count = usedServices.value.length;
  return t('shell.mobileBanner.servicesUsed', { count }, count);
});

const toggleServicesFilter = () => {
  toggleUsedOnly();
};

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};

const handleUpgradeClick = () => {
  showUpgradeModal.value = true;
};

const openTelegramChannel = () => {
  window.open(BANNER_CONSTANTS.TELEGRAM.URL, '_blank');
};

const handleTelegramButtonClick = (event: Event) => {
  event.stopPropagation();
  if (isPremiumUser.value) {
    openTelegramChannel();
  } else {
    handleUpgradeClick();
  }
};

const handleButtonClick = (event: Event) => {
  event.stopPropagation();
  if (props.bannerConfig?.variant === 'prelaunch') {
    handleTelegramButtonClick(event);
  } else if (props.bannerConfig?.buttonAction) {
    props.bannerConfig.buttonAction();
  }
};

const handleUpgrade = async () => {
  // Handled by UpgradeModal component
};

const handleSeeMore = () => {
  // Handled by UpgradeModal component
};
</script>

<style scoped>
.services-banner-height {
  height: 56px;
}

.prelaunch-title {
  font-family: Satoshi, sans-serif;
  font-weight: 700;
  line-height: 140%;
  letter-spacing: 0;
  vertical-align: middle;
}

.open-sans {
  font-family: 'Open Sans', sans-serif;
}

.banner-upgrade-link {
  background: transparent;
  border: none;
  padding: 0;
  font-size: inherit;
  color: inherit;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  font-weight: 600;
}
</style>
